/** Context window fill metrics for the composer ring (not billing token totals). */

export interface ContextUsage {
  usedTokens: number
  maxTokens: number
  percent: number
  peakInputTokens?: number
}

/** Default ring state before the first RUN_FINISHED (matches backend context_window_max_tokens). */
export const EMPTY_CONTEXT_USAGE: ContextUsage = {
  usedTokens: 0,
  maxTokens: 200_000,
  percent: 0,
}

export type ContextUsageSeverity = 'normal' | 'warning' | 'critical'

export const CONTEXT_SUMMARIZED_ALERT_MESSAGE =
  'Earlier parts of this conversation were summarized to free context space. Older tool details may no longer be in the model context.'

export function formatContextTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

/** Native tooltip for the send cluster (ring + submit). */
export function formatContextWindowTitle(percent: number): string {
  return `Context window ${percent}% full`
}

export function contextUsageSeverity(percent: number): ContextUsageSeverity {
  if (percent >= 85) return 'critical'
  if (percent >= 65) return 'warning'
  return 'normal'
}

/** Normalize wire or persisted JSON (snake_case or camelCase). */
export function normalizeContextUsage(raw: unknown): ContextUsage | undefined {
  let v: unknown = raw
  if (typeof raw === 'string') {
    try {
      v = JSON.parse(raw) as unknown
    } catch {
      return undefined
    }
  }
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const used = Number(o.used_tokens ?? o.usedTokens)
  const max = Number(o.max_tokens ?? o.maxTokens)
  const percent = Number(o.percent)
  if (!Number.isFinite(max) || max <= 0) return undefined
  const usedTokens = Number.isFinite(used) ? Math.max(0, used) : 0
  const maxTokens = max
  const pct = Number.isFinite(percent)
    ? Math.min(100, Math.max(0, percent))
    : Math.min(100, Math.round((100 * usedTokens) / maxTokens * 10) / 10)
  const peak = Number(o.peak_input_tokens ?? o.peakInputTokens)
  return {
    usedTokens,
    maxTokens,
    percent: pct,
    ...(Number.isFinite(peak) && peak > 0 ? {peakInputTokens: peak} : {}),
  }
}
